import { useEffect } from 'react';

import { Box, Button, Container, Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { navigate, useLocation, useParams } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import { BrownSideSVG, NPILogo, ResetPasswordSVG } from 'src/assets/images';
import { PasswordInput } from 'src/components/FormikComponents';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { RESET_TOKEN_EXPIRED } from 'src/graphql/mutations/login.mutation';
import { PASSWORD_REGEX, PASSWORD_REGEX_LIST } from 'src/utils/password-utils';

import './resetPage.css';

const ResetPasswordFormValidation = Yup.object().shape({
    newPassword: Yup.string()
        .required('Please Enter your password')
        .matches(PASSWORD_REGEX, 'Your password does not meet our strength requirements'),
    confirmPassword: Yup.string()
        .required('Confirm your password')
        .oneOf([Yup.ref('newPassword')], 'Your passwords do not match'),
});

interface ResetPasswordFormData {
    newPassword: string;
    confirmPassword: string;
}

const ResetPasswordPage = () => {
    const { resetPassword } = useAuthContext();
    const { resetToken } = useParams();
    const { pathname } = useLocation();
    const [inviteExpired] = useMutation(RESET_TOKEN_EXPIRED, {
        onCompleted: (res) => {
            if (res.inviteExpired) {
                showErrorToast('Your activation link has expired. Please contact support');
                navigate('/login', { replace: true });
            }
        },
    });

    const isResetPassword = pathname.includes('reset-password');

    const checkInviteExpiry = async (resetToken) => {
        await inviteExpired({ variables: { resetToken } });
    };

    const onResetPassword = async (
        values: ResetPasswordFormData,
        { setSubmitting }: FormikHelpers<ResetPasswordFormData>
    ) => {
        setSubmitting(true);
        if (resetPassword) {
            await resetPassword(values.newPassword, resetToken);
        }
    };

    useEffect(() => {
        checkInviteExpiry(resetToken);
    }, [resetToken, inviteExpired]);

    return (
        <>
            <MetaTags
                title={isResetPassword ? 'ResetPassword' : 'Create Password'}
                description={isResetPassword ? 'ResetPassword page' : 'CreatePassword page'}
            />

            <Container
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    width: '100vw',
                    justifyContent: 'center',
                }}
            >
                <Box
                    width="100%"
                    display="flex"
                    borderRadius="20px"
                    boxShadow="0 0 25px rgba(56, 11, 11, 0.29)"
                    overflow="hidden"
                    sx={{ backgroundColor: 'white' }}
                >
                    <Box display="flex" flex={0.3} position="relative">
                        <BrownSideSVG height="100%" width="100%" />
                        <Box
                            position="absolute"
                            top={0}
                            bottom={0}
                            width="100%"
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="center"
                            padding="20px"
                        >
                            <Box display="flex" width="100%" justifyContent="center">
                                <ResetPasswordSVG />
                            </Box>
                            <Typography
                                color="#FDFDFD"
                                fontSize="16px"
                                fontWeight="700"
                                textAlign="left"
                            >
                                Your Password Must Contain:
                            </Typography>

                            {PASSWORD_REGEX_LIST.map((listItem, index) => (
                                <Typography
                                    key={index}
                                    color="#FDFDFD"
                                    fontSize="16px"
                                    textAlign="left"
                                >{`• ${listItem}`}</Typography>
                            ))}
                        </Box>
                    </Box>
                    <Box display="flex" flex={0.7}>
                        <Box
                            maxWidth="70%"
                            margin="0 auto"
                            display="flex"
                            justifyContent="flex-start"
                            flexDirection="column"
                        >
                            <Formik
                                initialValues={{ newPassword: '', confirmPassword: '' }}
                                onSubmit={onResetPassword}
                                validationSchema={ResetPasswordFormValidation}
                            >
                                {({ isValid, dirty, isSubmitting }) => {
                                    const disableButton = !dirty || !isValid || isSubmitting;
                                    return (
                                        <Form>
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                gap="32px"
                                                paddingTop="52px"
                                                width="100%"
                                            >
                                                <NPILogo />
                                                <Typography
                                                    fontWeight="500"
                                                    fontSize="32px"
                                                    color="black"
                                                >
                                                    {isResetPassword
                                                        ? 'RESET PASSWORD'
                                                        : 'CREATE PASSWORD'}
                                                </Typography>
                                                <Typography fontSize="16px" color="#4d4d4d">
                                                    Enter your new password below
                                                </Typography>
                                                <PasswordInput
                                                    label="Password"
                                                    name="newPassword"
                                                    placeholder="Create new password"
                                                />
                                                <PasswordInput
                                                    label="Password"
                                                    name="confirmPassword"
                                                    placeholder="Confirm new password"
                                                />
                                                <Button
                                                    color="primary"
                                                    disabled={disableButton}
                                                    sx={{
                                                        bgcolor: disableButton
                                                            ? 'rgba(0, 0, 0, 0.12)'
                                                            : '#000',
                                                        color: disableButton
                                                            ? 'rgba(0, 0, 0, 0.26)'
                                                            : '#fff',
                                                        '&:hover': {
                                                            bgcolor: disableButton
                                                                ? 'rgba(0, 0, 0, 0.12)'
                                                                : '#000',
                                                        },
                                                    }}
                                                    type="submit"
                                                >
                                                    {isResetPassword
                                                        ? 'RESET PASSWORD'
                                                        : 'CREATE PASSWORD'}
                                                </Button>
                                            </Box>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default ResetPasswordPage;
